import { useCallback, useRef, useState } from 'react';
import ActivityListSidebar from '../components/activities/ActivityListSidebar';
import ActivityCreatePanel from '../components/activities/ActivityCreatePanel';
import ActivityEditPanel from '../components/activities/ActivityEditPanel';
import UnsavedChangesModal from '../components/activities/UnsavedChangesModal';
import { useActivities, useActivity } from '../hooks/useActivities';
import { useActivityFilters } from '../hooks/useActivityFilters';

type PendingAction = { type: 'select'; id: number } | { type: 'new' } | null;

export default function ActivitiesPage() {
  const { filters, updateFilter, clearFilters } = useActivityFilters();
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [pendingAction, setPendingAction] = useState<PendingAction>(null);
  const isDirtyRef = useRef(false);

  const { data: listData, isLoading } = useActivities(filters);
  const activities = listData?.items ?? [];

  const selectedIndex = selectedId !== null ? activities.findIndex((a) => a.id === selectedId) : -1;
  const { data: selectedActivity } = useActivity(selectedId);

  const handleDirtyChange = useCallback((dirty: boolean) => {
    isDirtyRef.current = dirty;
  }, []);

  const runAction = (action: NonNullable<PendingAction>) => {
    isDirtyRef.current = false;
    if (action.type === 'select') {
      setIsCreating(false);
      setSelectedId(action.id);
    } else {
      setSelectedId(null);
      setIsCreating(true);
    }
  };

  const guarded = (action: NonNullable<PendingAction>) => {
    if (isDirtyRef.current) {
      setPendingAction(action);
    } else {
      runAction(action);
    }
  };

  const handleSelect = (id: number) => {
    if (id === selectedId && !isCreating) return;
    guarded({ type: 'select', id });
  };

  const handleNewClick = () => {
    if (isCreating) return;
    guarded({ type: 'new' });
  };

  // Prev/next are guarded by the edit panel's own unsaved-changes modal
  const handlePrev = () => {
    if (selectedIndex > 0) setSelectedId(activities[selectedIndex - 1].id);
  };

  const handleNext = () => {
    if (selectedIndex >= 0 && selectedIndex < activities.length - 1) {
      setSelectedId(activities[selectedIndex + 1].id);
    }
  };

  const handleCreated = (id: number) => {
    isDirtyRef.current = false;
    setIsCreating(false);
    setSelectedId(id);
  };

  const handleCancelCreate = () => {
    isDirtyRef.current = false;
    setIsCreating(false);
  };

  const handleArchived = () => {
    // Every status filter except "Archived" hides archived activities
    if (filters.status !== 'archived') setSelectedId(null);
  };

  return (
    <div className="flex h-[calc(100vh-64px)] gap-[22px] p-[22px]">
      <ActivityListSidebar
        filters={filters}
        onUpdateFilter={updateFilter}
        onClearFilters={clearFilters}
        activities={activities}
        selectedId={selectedId}
        onSelect={handleSelect}
        isLoading={isLoading}
        onNewClick={handleNewClick}
        collapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed((v) => !v)}
      />

      <main className="flex-1 overflow-y-auto bg-pine-paper min-w-0">
        {isCreating ? (
          <ActivityCreatePanel
            onCancel={handleCancelCreate}
            onCreated={handleCreated}
            onDirtyChange={handleDirtyChange}
          />
        ) : selectedActivity ? (
          <ActivityEditPanel
            activity={selectedActivity}
            currentIndex={selectedIndex}
            total={activities.length}
            onPrev={handlePrev}
            onNext={handleNext}
            onDirtyChange={handleDirtyChange}
            onArchived={handleArchived}
          />
        ) : (
          <div className="flex items-center justify-center h-full">
            <div className="text-center text-pine-ink-soft">
              <p className="text-sm">Select an activity to view details</p>
              {activities.length === 0 && !isLoading && (
                <p className="text-xs mt-2">
                  <button onClick={handleNewClick} className="underline hover:text-pine-ink">
                    Create your first activity
                  </button>
                </p>
              )}
            </div>
          </div>
        )}
      </main>

      <UnsavedChangesModal
        open={pendingAction !== null}
        onLeave={() => {
          const action = pendingAction;
          setPendingAction(null);
          if (action) runAction(action);
        }}
        onStay={() => setPendingAction(null)}
      />
    </div>
  );
}
